import * as apiHelper from 'api/ApiHelper';
import { getToken } from 'services/auth';

const HOST = process.env.REACT_APP_API_HOST || '';
const CHAT = `${HOST}/api/chat`;
const ADMIN = `${HOST}/api/admin/chat`;

const withCommunity = (communityId) => ({ communityId });

export const chatApi = {
    list: (communityId) => apiHelper.get(`${CHAT}/conversations`, withCommunity(communityId)),
    unread: (communityId) => apiHelper.get(`${CHAT}/unread`, withCommunity(communityId)),
    history: (communityId, conversationId, beforeSeq) =>
        apiHelper.get(`${CHAT}/conversations/${conversationId}/messages`, { communityId, beforeSeq }),
    afterSeq: (communityId, conversationId, afterSeq) =>
        apiHelper.get(`${CHAT}/conversations/${conversationId}/messages/after`, { communityId, afterSeq }),
    send: (communityId, conversationId, text, clientMsgId, replyToSeq) =>
        apiHelper.post(`${CHAT}/conversations/${conversationId}/messages`, withCommunity(communityId), {
            text,
            clientMsgId,
            communityId,
            ...(replyToSeq != null ? { replyToSeq } : {}),
        }),
    read: (communityId, conversationId, seq) =>
        apiHelper.post(`${CHAT}/conversations/${conversationId}/read`, withCommunity(communityId), { seq }),
    mute: (communityId, conversationId, until) =>
        apiHelper.post(`${CHAT}/conversations/${conversationId}/mute`, withCommunity(communityId), { until }),
    hide: (communityId, conversationId) =>
        apiHelper.post(`${CHAT}/conversations/${conversationId}/hide`, withCommunity(communityId)),
    leave: (communityId, conversationId) =>
        apiHelper.post(`${CHAT}/conversations/${conversationId}/leave`, withCommunity(communityId)),
    pin: (communityId, conversationId) =>
        apiHelper.post(`${CHAT}/conversations/${conversationId}/pin`, withCommunity(communityId)),
    unpin: (communityId, conversationId) =>
        apiHelper.post(`${CHAT}/conversations/${conversationId}/unpin`, withCommunity(communityId)),
    typing: (communityId, conversationId) =>
        apiHelper.post(`${CHAT}/conversations/${conversationId}/typing`, withCommunity(communityId)),
    toggleReaction: (communityId, conversationId, seq, emoji) =>
        apiHelper.post(
            `${CHAT}/conversations/${conversationId}/messages/${seq}/reactions`,
            withCommunity(communityId),
            { emoji },
        ),
    deleteMessage: (communityId, conversationId, seq) =>
        apiHelper.del(`${CHAT}/conversations/${conversationId}/messages/${seq}`, withCommunity(communityId)),
    editMessage: (communityId, conversationId, seq, text) =>
        apiHelper.patch(`${CHAT}/conversations/${conversationId}/messages/${seq}`, withCommunity(communityId), { text }),
    eligibility: (communityId, targetParticipantId) =>
        apiHelper.get(`${CHAT}/intros/eligibility`, { communityId, targetParticipantId }),
    introsEnabled: (communityId) =>
        apiHelper.get(`${CHAT}/intros/enabled`, withCommunity(communityId)),
    requestIntro: (communityId, targetParticipantId, note) =>
        apiHelper.post(`${CHAT}/intros`, withCommunity(communityId), { targetParticipantId, note }),
    cancelIntro: (communityId, requestId) =>
        apiHelper.post(`${CHAT}/intros/${requestId}/cancel`, withCommunity(communityId)),
    block: (communityId, targetParticipantId, reason) =>
        apiHelper.post(`${CHAT}/blocks`, withCommunity(communityId), { targetParticipantId, reason }),
    unblock: (communityId, targetParticipantId) =>
        apiHelper.del(`${CHAT}/blocks/${targetParticipantId}`, withCommunity(communityId)),
    getNotificationSettings: (communityId) =>
        apiHelper.get(`${CHAT}/notification-settings`, withCommunity(communityId)),
    saveNotificationSettings: (communityId, body) =>
        apiHelper.put(`${CHAT}/notification-settings`, withCommunity(communityId), body),
    lookupOrganizerConversation: (communityId, peerParticipantId) =>
        apiHelper.get(`${CHAT}/organizer-conversations`, { communityId, peerParticipantId }),
    sendOrganizerMessage: (communityId, peerParticipantId, text, clientMsgId) =>
        apiHelper.post(`${CHAT}/organizer-conversations/messages`, withCommunity(communityId), {
            peerParticipantId,
            text,
            clientMsgId,
        }),
};

export const adminChatApi = {
    queue: (communityId, page = 0, includeArchived = false) =>
        apiHelper.get(`${ADMIN}/intros`, { communityId, page, includeArchived }),
    pendingCount: (communityId) => apiHelper.get(`${ADMIN}/intros/pending-count`, withCommunity(communityId)),
    approve: (communityId, requestId, body) =>
        apiHelper.post(`${ADMIN}/intros/${requestId}/approve`, withCommunity(communityId), body || {}),
    archive: (communityId, requestId) =>
        apiHelper.post(`${ADMIN}/intros/${requestId}/archive`, withCommunity(communityId)),
    unarchive: (communityId, requestId) =>
        apiHelper.post(`${ADMIN}/intros/${requestId}/unarchive`, withCommunity(communityId)),
    translateIcebreaker: (communityId, body) =>
        apiHelper.post(`${ADMIN}/intros/translate-icebreaker`, withCommunity(communityId), body),
    reject: (communityId, requestId, body) =>
        apiHelper.post(`${ADMIN}/intros/${requestId}/reject`, withCommunity(communityId), body || {}),
    conversations: (communityId, page = 0, participantId) =>
        apiHelper.get(`${ADMIN}/conversations`, {
            communityId,
            page,
            ...(participantId ? { participantId } : {}),
        }),
    searchParticipants: (communityId, search) =>
        apiHelper.get(`${ADMIN}/participants`, { communityId, search }),
    createConversation: (communityId, body) =>
        apiHelper.post(`${ADMIN}/conversations`, withCommunity(communityId), body),
    lock: (communityId, conversationId, reason) =>
        apiHelper.post(`${ADMIN}/conversations/${conversationId}/lock`, withCommunity(communityId), { reason }),
    unlock: (communityId, conversationId) =>
        apiHelper.post(`${ADMIN}/conversations/${conversationId}/unlock`, withCommunity(communityId)),
    deleteConversation: (communityId, conversationId, reason) =>
        apiHelper.del(`${ADMIN}/conversations/${conversationId}`, withCommunity(communityId), { reason }),
    deleteMessage: (communityId, conversationId, seq, reason) =>
        apiHelper.del(`${ADMIN}/conversations/${conversationId}/messages/${seq}`, withCommunity(communityId), { reason }),
    sendBotMessage: (communityId, conversationId, text, purpose) =>
        apiHelper.post(`${ADMIN}/conversations/${conversationId}/bot-messages`, withCommunity(communityId), { text, purpose }),
    scenarios: (communityId) => apiHelper.get(`${ADMIN}/settings/scenarios`, withCommunity(communityId)),
    saveScenario: (communityId, body) => apiHelper.put(`${ADMIN}/settings/scenarios`, withCommunity(communityId), body),
    organizerMessages: (communityId) => apiHelper.get(`${ADMIN}/settings/organizer-messages`, withCommunity(communityId)),
    saveOrganizerMessages: (communityId, body) =>
        apiHelper.put(`${ADMIN}/settings/organizer-messages`, withCommunity(communityId), body),
    templates: (communityId) => apiHelper.get(`${ADMIN}/settings/templates`, withCommunity(communityId)),
    saveTemplate: (communityId, body) => apiHelper.put(`${ADMIN}/settings/templates`, withCommunity(communityId), body),
    previewTemplate: (communityId, body) => apiHelper.post(`${ADMIN}/settings/templates/preview`, withCommunity(communityId), body),
    previewIntro: (communityId, requestId, body) =>
        apiHelper.post(`${ADMIN}/intros/${requestId}/preview`, withCommunity(communityId), body || {}),
    defaultTemplate: (communityId, lang, kind) =>
        apiHelper.get(`${ADMIN}/settings/templates/default`, { communityId, lang, kind }),
    aiSettings: (communityId) => apiHelper.get(`${ADMIN}/settings/ai`, withCommunity(communityId)),
    saveAiSettings: (communityId, body) => apiHelper.put(`${ADMIN}/settings/ai`, withCommunity(communityId), body),
    aiProviders: (communityId) => apiHelper.get(`${ADMIN}/settings/ai/providers`, withCommunity(communityId)),
    bot: (communityId) => apiHelper.get(`${ADMIN}/settings/bot`, withCommunity(communityId)),
    saveBot: async (communityId, bot, avatarFile) => {
        const form = new FormData();
        form.append('bot', new Blob([JSON.stringify(bot)], { type: 'application/json' }));
        if (avatarFile) {
            form.append('avatar', avatarFile);
        }
        const axios = require('axios');
        const { getMyIp } = require('services/ip');
        const { getDomain } = require('utils');
        const response = await axios.put(`${ADMIN}/settings/bot`, form, {
            params: { communityId },
            headers: {
                Authorization: `Bearer ${getToken()}`,
                Community: getDomain(),
                'Public-Ip': await getMyIp(),
            },
        });
        return response.data;
    },
    notifications: (communityId) => apiHelper.get(`${ADMIN}/settings/notifications`, withCommunity(communityId)),
    saveNotifications: (communityId, body) =>
        apiHelper.put(`${ADMIN}/settings/notifications`, withCommunity(communityId), body),
    adminParticipants: (communityId, search) =>
        apiHelper.get(`${HOST}/api/admin/${communityId}/participants`, { search }),
};
